import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { MatDialogModule, MAT_DIALOG_DATA, MatDialogRef, MatDialog } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTabsModule } from '@angular/material/tabs';
import { MatChipsModule } from '@angular/material/chips';
import { MatCardModule } from '@angular/material/card';
import { MatExpansionModule } from '@angular/material/expansion';
import { environment } from '../../../environments/environment';
import { ToastService } from '../../services/toast.service';
import { SharedServiceService } from '../../services/shared-service.service';
import { Subscription, forkJoin } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { throwError } from 'rxjs';

// Constants for form field names and default values
const FORM_FIELDS = {
  INGREDIENTS: 'ingredients',
  SELECTED_PRODUCT: 'selected_product',
  NAME: 'name',
  QUANTITY: 'quantity',
  UNIT: 'unit',
  VARIANTS: 'variants',
  SHELF_LIFE_CATEGORY: 'shelf_life_category',
  SHELF_LIFE_DISPLAY: 'shelf_life_display',
  SHELF_LIFE_EMOJI: 'shelf_life_emoji',
  PRODUCT_NAME: 'product_name',
  STORE: 'store',
  PRICE: 'price',
  SIZE: 'size',
  HEALTH_SCORE: 'health_score',
  REASON: 'reason',
  GREEN_FLAGS: 'green_flags',
  RED_FLAGS: 'red_flags',
  ALTERNATIVES: 'alternatives'
} as const;

const DEFAULT_VALUES = {
  STORE_RECOMMENDATION: 'No specific store recommended',
  SHELF_LIFE_CATEGORY: 'long',
  SHELF_LIFE_DISPLAY: 'Long (>20 days)',
  SHELF_LIFE_EMOJI: '🟢',
  HEALTH_SCORE_MIN: 0,
  HEALTH_SCORE_MAX: 100,
  HEALTH_SCORE_DEFAULT: 0,
  EMPTY_STRING: '',
  EMPTY_ARRAY: []
} as const;

@Component({
  selector: 'app-grocery-editor-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTabsModule,
    MatChipsModule,
    MatCardModule,
    MatExpansionModule
  ],
  template: `
    <div class="grocery-editor-container">
      <h2 mat-dialog-title>
        <mat-icon>shopping_cart</mat-icon>
        Edit Your Grocery List
      </h2>

      <mat-dialog-content *ngIf="!loading && groceryForm">
        <div class="store-info">
          <h3>🏬 Recommended Store: {{ recommendedStore }}</h3>
        </div>

        <div class="health-legend">
          <div class="legend-item">
            <span class="legend-dot legend-dot-great"></span>
            <span>85–100: 🟢 Great Choice</span>
          </div>
          <div class="legend-item">
            <span class="legend-dot legend-dot-good"></span>
            <span>70–84: 🟡 Good Choice</span>
          </div>
          <div class="legend-item">
            <span class="legend-dot legend-dot-limit"></span>
            <span>0–69: 🔴 Limit / Occasional</span>
          </div>
        </div>

        <form [formGroup]="groceryForm">
          <div formArrayName="ingredients" class="ingredients-list">
            <mat-expansion-panel *ngFor="let ingredient of ingredientsFormArray.controls; let i = index" class="ingredient-card">
              <mat-expansion-panel-header>
                <mat-panel-title>
                  <span class="shelf-life-badge" [attr.data-category]="getIngredientValue(i, 'shelf_life_category')">
                    {{ getIngredientValue(i, 'shelf_life_emoji') }}
                  </span>
                  <strong>{{ capitalizeText(getIngredientValue(i, 'name')) }}</strong>
                  <span class="quantity-badge">
                    {{ getIngredientValue(i, 'quantity') }} {{ getIngredientValue(i, 'unit') }}
                  </span>
                  <span class="price-badge" *ngIf="getProductValue(i, 'price')">
                    {{ getProductValue(i, 'price') | currency }}
                  </span>
                  <span
                    *ngIf="getProductValue(i, 'health_score') !== null && getProductValue(i, 'health_score') !== undefined"
                    class="health-badge"
                    [ngClass]="getHealthBadgeClass(getProductValue(i, 'health_score'))"
                  >
                    {{ getProductValue(i, 'health_score') }}/100
                  </span>
                </mat-panel-title>
              </mat-expansion-panel-header>

              <div [formGroupName]="i" class="ingredient-form">
                <!-- Ingredient Name and Quantity -->
                <div class="form-row">
                  <mat-form-field appearance="outline" class="full-width">
                    <mat-label>Ingredient Name</mat-label>
                    <input matInput formControlName="name" placeholder="e.g., Eggs">
                    <mat-error *ngIf="ingredientsFormArray.at(i).get('name')?.hasError('required')">
                      Ingredient name is required
                    </mat-error>
                  </mat-form-field>
                </div>

                <div class="form-row">
                  <mat-form-field appearance="outline" class="quantity-field">
                    <mat-label>Quantity</mat-label>
                    <input matInput type="number" formControlName="quantity" min="0" step="0.1">
                    <mat-error *ngIf="ingredientsFormArray.at(i).get('quantity')?.hasError('required')">
                      Quantity is required
                    </mat-error>
                    <mat-error *ngIf="ingredientsFormArray.at(i).get('quantity')?.hasError('min')">
                      Quantity must be 0 or greater
                    </mat-error>
                  </mat-form-field>
                  <mat-form-field appearance="outline" class="unit-field">
                    <mat-label>Unit</mat-label>
                    <input matInput formControlName="unit" placeholder="e.g., cups, lbs">
                    <mat-error *ngIf="ingredientsFormArray.at(i).get('unit')?.hasError('required')">
                      Unit is required
                    </mat-error>
                  </mat-form-field>
                </div>

                <!-- Variants -->
                <div *ngIf="getIngredientValue(i, 'variants')?.length > 0" class="variants-section">
                  <label class="variants-label">Original variants from meal plan:</label>
                  <div class="chip-list">
                    <mat-chip *ngFor="let variant of getIngredientValue(i, 'variants')" class="variant-chip">
                      {{ variant }}
                    </mat-chip>
                  </div>
                </div>

                <!-- Selected Product -->
                <div formGroupName="selected_product" class="product-section">
                  <h4>Selected Product</h4>
                  
                  <mat-form-field appearance="outline" class="full-width">
                    <mat-label>Product Name</mat-label>
                    <input matInput formControlName="product_name">
                  </mat-form-field>

                  <div class="form-row">
                    <mat-form-field appearance="outline" class="store-field">
                      <mat-label>Store</mat-label>
                      <input matInput formControlName="store">
                    </mat-form-field>
                    <mat-form-field appearance="outline" class="price-field">
                      <mat-label>Price</mat-label>
                      <input matInput type="number" formControlName="price" min="0" step="0.01">
                      <mat-error *ngIf="getProductFieldControl(i, 'price')?.hasError('min')">
                        Price must be 0 or greater
                      </mat-error>
                    </mat-form-field>
                  </div>

                  <div class="form-row">
                    <mat-form-field appearance="outline" class="size-field">
                      <mat-label>Size</mat-label>
                      <input matInput formControlName="size">
                    </mat-form-field>
                    <mat-form-field appearance="outline" class="health-score-field">
                      <mat-label>Health Score</mat-label>
                      <input matInput type="number" formControlName="health_score" min="0" max="100">
                      <mat-error *ngIf="getProductFieldControl(i, 'health_score')?.hasError('min')">
                        Health score must be 0 or greater
                      </mat-error>
                      <mat-error *ngIf="getProductFieldControl(i, 'health_score')?.hasError('max')">
                        Health score must be 100 or less
                      </mat-error>
                    </mat-form-field>
                  </div>

                  <!-- Green Flags -->
                  <div *ngIf="getProductValue(i, 'green_flags')?.length > 0" class="flags-section">
                    <label class="flags-label">✅ Green Flags:</label>
                    <div class="chip-list">
                      <mat-chip *ngFor="let flag of getProductValue(i, 'green_flags')" class="green-chip">
                        {{ flag }}
                      </mat-chip>
                    </div>
                  </div>

                  <!-- Red Flags -->
                  <div *ngIf="getProductValue(i, 'red_flags')?.length > 0" class="flags-section">
                    <label class="flags-label">⚠️ Red Flags:</label>
                    <div class="chip-list">
                      <mat-chip *ngFor="let flag of getProductValue(i, 'red_flags')" class="red-chip">
                        {{ flag }}
                      </mat-chip>
                    </div>
                  </div>
                </div>

                <!-- Alternative Products -->
                <div *ngIf="getIngredientValue(i, 'alternatives')?.length > 0" class="alternatives-section">
                  <h4>Alternative Products</h4>
                  <div class="alternatives-list">
                    <mat-card *ngFor="let alt of getIngredientValue(i, 'alternatives'); let altIdx = index" 
                              class="alternative-card"
                              (click)="selectAlternative(i, altIdx)">
                      <mat-card-content>
                        <div class="alt-header">
                          <strong>{{ alt.product_name }}</strong>
                          <span
                            class="health-score"
                            [ngClass]="getHealthBadgeClass(alt.health_score)"
                          >
                            {{ alt.health_score }}/100
                          </span>
                        </div>
                        <div class="alt-details">
                          <span>{{ alt.store }}</span>
                          <span *ngIf="alt.price">{{ alt.price | currency }}</span>
                          <span *ngIf="alt.size">{{ alt.size }}</span>
                        </div>
                        <p class="alt-reason">{{ alt.reason }}</p>
                        <button mat-raised-button color="primary" type="button" class="select-btn">
                          Select This Product
                        </button>
                      </mat-card-content>
                    </mat-card>
                  </div>
                </div>
              </div>
            </mat-expansion-panel>
          </div>
        </form>

        <div class="totals-summary" *ngIf="ingredientsFormArray?.length">
          <div class="totals-row">
            <span class="totals-label">Total approximate amount:</span>
            <span class="totals-value">
              {{ getTotalApproxAmount() | currency }}
            </span>
          </div>
          <div class="totals-row" *ngIf="getTotalApproxSavings() > 0">
            <span class="totals-label">Total approximate savings:</span>
            <span class="totals-value totals-savings">
              {{ getTotalApproxSavings() | currency }}
            </span>
          </div>
        </div>
      </mat-dialog-content>

      <div *ngIf="loading" class="loading-container">
        <mat-spinner></mat-spinner>
        <p>Loading your grocery list...</p>
      </div>

      <mat-dialog-actions align="end" class="dialog-actions">
        <button mat-button (click)="onCancel()" [disabled]="saving">
          Cancel
        </button>
        <button mat-raised-button color="accent" (click)="onSave()" [disabled]="saving || loading">
          <mat-icon *ngIf="!saving">save</mat-icon>
          <mat-spinner *ngIf="saving" diameter="20"></mat-spinner>
          {{ saving ? 'Saving...' : 'Save Changes' }}
        </button>
        <button mat-raised-button color="primary" (click)="onDownloadPDF()" [disabled]="saving || loading">
          <mat-icon>download</mat-icon>
          Download PDF
        </button>
      </mat-dialog-actions>
    </div>
  `,
  styles: [`
    .grocery-editor-container {
      max-height: 75vh;
      min-height: 400px;
      width: 100%;
      max-width: 1200px;
      display: flex;
      flex-direction: column;
    }

    h2[mat-dialog-title] {
      display: flex;
      align-items: center;
      gap: 12px;
      color: #05445E;
      margin: 0;
      padding: 20px 24px;
      border-bottom: 2px solid #e0e0e0;
    }

    mat-dialog-content {
      flex: 1;
      overflow-y: auto;
      padding: 24px;
    }

    .store-info {
      background: linear-gradient(135deg, #05445E 0%, #0a7ea4 100%);
      color: white;
      padding: 16px;
      border-radius: 8px;
      margin-bottom: 24px;
      text-align: center;
    }

    .store-info h3 {
      margin: 0;
      font-size: 14px;
    }

    .ingredients-list {
      display: flex;
      flex-direction: column;
      gap: 16px;
    }

    .ingredient-card {
      box-shadow: 0 2px 8px rgba(0,0,0,0.1);
    }

    .quantity-badge {
      background: #05445E;
      color: white;
      padding: 4px 12px;
      border-radius: 12px;
      font-size: 12px;
      margin-left: auto;
    }

    .shelf-life-badge {
      font-size: 16px;
      margin-right: 8px;
      display: inline-flex;
      align-items: center;
    }

    .shelf-life-badge[data-category="short"] {
      filter: drop-shadow(0 0 2px rgba(255, 0, 0, 0.5));
    }

    .shelf-life-badge[data-category="medium"] {
      filter: drop-shadow(0 0 2px rgba(255, 193, 7, 0.5));
    }

    .shelf-life-badge[data-category="long"] {
      filter: drop-shadow(0 0 2px rgba(76, 175, 80, 0.5));
    }

    .ingredient-form {
      padding: 16px 0;
    }

    .health-legend {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      margin-bottom: 16px;
      font-size: 12px;
      color: #333;
    }

    .legend-item {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 4px 8px;
      border-radius: 12px;
      background: #f5f5f5;
    }

    .legend-dot {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      display: inline-block;
    }

    .legend-dot-great {
      background: #4caf50;
    }

    .legend-dot-good {
      background: #ffb300;
    }

    .legend-dot-limit {
      background: #e53935;
    }

    .form-row {
      display: flex;
      gap: 16px;
      margin-bottom: 8px;
    }

    .full-width {
      width: 100%;
    }

    .quantity-field {
      flex: 2;
    }

    .unit-field {
      flex: 1;
    }

    .store-field {
      flex: 2;
    }

    .price-field {
      flex: 1;
    }

    .size-field {
      flex: 1;
    }

    .health-score-field {
      flex: 1;
    }

    .variants-section, .flags-section {
      margin: 16px 0;
    }

    .variants-label, .flags-label {
      display: block;
      font-size: 14px;
      font-weight: 500;
      margin-bottom: 8px;
      color: #666;
    }

    .chip-list {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
    }

    .variant-chip {
      background: #e3f2fd;
      color: #1976d2;
      font-size: 12px;
      height: 28px;
    }

    .green-chip {
      background: #e8f5e9;
      color: #2e7d32;
    }

    .red-chip {
      background: #ffebee;
      color: #c62828;
    }

    .product-section {
      background: #f5f5f5;
      padding: 16px;
      border-radius: 8px;
      margin: 16px 0;
    }

    .product-section h4 {
      margin: 0 0 16px 0;
      color: #05445E;
    }

    .alternatives-section {
      margin-top: 24px;
    }

    .alternatives-section h4 {
      color: #05445E;
      margin-bottom: 16px;
    }

    .alternatives-list {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
      gap: 16px;
    }

    .alternative-card {
      cursor: pointer;
      transition: all 0.2s;
      border: 2px solid transparent;
    }

    .alternative-card:hover {
      transform: translateY(-4px);
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      border-color: #05445E;
    }

    .alt-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 8px;
    }

    .health-score {
      padding: 4px 8px;
      border-radius: 4px;
      font-size: 12px;
      font-weight: bold;
    }

    .alt-details {
      display: flex;
      gap: 12px;
      font-size: 13px;
      color: #666;
      margin-bottom: 8px;
    }

    .alt-reason {
      font-size: 13px;
      color: #444;
      margin: 8px 0 12px 0;
      line-height: 1.4;
    }

    .select-btn {
      width: 100%;
      margin-top: 8px;
    }

    .loading-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 60px;
      gap: 20px;
    }

    .dialog-actions {
      border-top: 1px solid #e0e0e0;
      padding: 16px 24px;
      gap: 12px;
    }

    mat-form-field {
      font-size: 14px;
    }

    mat-expansion-panel-header {
      background: #fafafa;
    }

    mat-expansion-panel-header:hover {
      background: #f0f0f0;
    }

    .price-badge {
      background: #ffffff;
      color: #05445E;
      padding: 4px 10px;
      border-radius: 12px;
      font-size: 12px;
      margin-left: 8px;
      border: 1px solid #d0d7de;
    }

    .health-badge {
      margin-left: 8px;
      font-size: 12px;
      padding: 4px 8px;
      border-radius: 12px;
      font-weight: 600;
      color: #ffffff;
    }

    .health-badge-great {
      background-color: #2e7d32;
    }

    .health-badge-good {
      background-color: #ffb300;
      color: #212121;
    }

    .health-badge-limit {
      background-color: #c62828;
    }

    .health-badge-neutral {
      background-color: #9e9e9e;
    }

    .totals-summary {
      margin-top: 24px;
      padding: 16px;
      border-radius: 8px;
      background: #f9fafb;
      border: 1px solid #e0e0e0;
    }

    .totals-row {
      display: flex;
      justify-content: space-between;
      margin-bottom: 4px;
      font-size: 14px;
    }

    .totals-label {
      font-weight: 500;
      color: #555;
    }

    .totals-value {
      font-weight: 600;
      color: #05445E;
    }

    .totals-savings {
      color: #2e7d32;
    }
  `]
})
export class GroceryEditorDialogComponent implements OnInit, OnDestroy {
  loading = true;
  saving = false;
  groceryForm: FormGroup;
  recommendedStore = '';
  private groceryApiUrl: string = environment.groceryListApiUrl;
  private subscriptions: Subscription[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { memberId: string },
    private dialogRef: MatDialogRef<GroceryEditorDialogComponent>,
    private http: HttpClient,
    private fb: FormBuilder,
    private toastService: ToastService,
    private dialog: MatDialog
  ) {
    // Initialize form early to prevent undefined errors in template
    this.groceryForm = this.fb.group({
      ingredients: this.fb.array([])
    });
  }

  ngOnInit() {
    this.loadGroceryData();
  }

  ngOnDestroy() {
    // Cleanup all subscriptions to prevent memory leaks
    this.subscriptions.forEach(sub => {
      if (sub && !sub.closed) {
        sub.unsubscribe();
      }
    });
  }

  get ingredientsFormArray(): FormArray {
    return this.groceryForm.get(FORM_FIELDS.INGREDIENTS) as FormArray;
  }

  getIngredientValue(index: number, field: string): any {
    const control = this.ingredientsFormArray.at(index)?.get(field);
    return control?.value ?? null;
  }

  getProductValue(index: number, field: string): any {
    const selectedProductGroup = this.ingredientsFormArray.at(index)?.get(FORM_FIELDS.SELECTED_PRODUCT);
    return selectedProductGroup?.get(field)?.value ?? null;
  }

  getProductFieldControl(index: number, field: string): any {
    const selectedProductGroup = this.ingredientsFormArray.at(index)?.get(FORM_FIELDS.SELECTED_PRODUCT);
    return selectedProductGroup?.get(field) ?? null;
  }

  getHealthBadgeClass(score: number | string | null | undefined): string {
    const value = this.toNumber(score);
    if (value >= 85) {
      return 'health-badge-great';
    }
    if (value >= 70) {
      return 'health-badge-good';
    }
    if (value >= 0) {
      return 'health-badge-limit';
    }
    return 'health-badge-neutral';
  }

  capitalizeText(text: string): string {
    if (!text) return '';
    return text.split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  private loadGroceryData() {
    // Call grocery API directly - CORS is configured
    const sub = this.http.get<any>(`${this.groceryApiUrl}/get_editable_results/${this.data.memberId}`)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          this.handleError('load', error);
          return throwError(() => error);
        }),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe({
        next: (data) => {
          if (data && data.ingredients && Array.isArray(data.ingredients)) {
            this.recommendedStore = data.recommended_store || DEFAULT_VALUES.STORE_RECOMMENDATION;
            this.buildForm(data);
          } else {
            this.toastService.errorMessage('Invalid data received. Please try again.');
            this.dialogRef.close();
          }
        },
        error: () => {
          // Error already handled in catchError operator
          this.dialogRef.close();
        }
      });
    this.subscriptions.push(sub);
  }

  private handleError(operation: string, error: HttpErrorResponse): void {
    let errorMessage = 'An unexpected error occurred.';
    
    if (error.error instanceof ErrorEvent) {
      // Client-side error
      errorMessage = `Network error: ${error.error.message}`;
    } else {
      // Server-side error
      switch (error.status) {
        case 0:
          errorMessage = 'Unable to connect to server. Please check your internet connection.';
          break;
        case 404:
          errorMessage = 'Grocery list not found. It may not be ready yet.';
          break;
        case 500:
          errorMessage = 'Server error. Please try again later.';
          break;
        default:
          errorMessage = error.error?.detail || error.error?.message || `Error ${error.status}: ${error.statusText}`;
      }
    }

    console.error(`Error during ${operation}:`, error);
    
    const actionMessages: Record<string, string> = {
      'load': `Failed to load grocery list: ${errorMessage}`,
      'save': `Failed to save changes: ${errorMessage}`,
      'download': `Failed to download PDF: ${errorMessage}`
    };

    this.toastService.errorMessage(actionMessages[operation] || errorMessage);
  }

  private buildForm(data: { ingredients: any[]; recommended_store?: string }) {
    if (!data || !data.ingredients || !Array.isArray(data.ingredients)) {
      this.toastService.errorMessage('Invalid grocery data format.');
      this.dialogRef.close();
      return;
    }

    const ingredientsArray = this.fb.array(
      data.ingredients.map((ing: any) => this.createIngredientFormGroup(ing))
    );

    this.groceryForm = this.fb.group({
      [FORM_FIELDS.INGREDIENTS]: ingredientsArray
    });
  }

  private createIngredientFormGroup(ingredient: any): FormGroup {
    return this.fb.group({
      [FORM_FIELDS.NAME]: [
        ingredient[FORM_FIELDS.NAME] || DEFAULT_VALUES.EMPTY_STRING,
        Validators.required
      ],
      [FORM_FIELDS.QUANTITY]: [
        ingredient[FORM_FIELDS.QUANTITY] ?? 0,
        [Validators.required, Validators.min(0)]
      ],
      [FORM_FIELDS.UNIT]: [
        ingredient[FORM_FIELDS.UNIT] || DEFAULT_VALUES.EMPTY_STRING,
        Validators.required
      ],
      [FORM_FIELDS.VARIANTS]: [ingredient[FORM_FIELDS.VARIANTS] || DEFAULT_VALUES.EMPTY_ARRAY],
      [FORM_FIELDS.SHELF_LIFE_CATEGORY]: [
        ingredient[FORM_FIELDS.SHELF_LIFE_CATEGORY] || DEFAULT_VALUES.SHELF_LIFE_CATEGORY
      ],
      [FORM_FIELDS.SHELF_LIFE_DISPLAY]: [
        ingredient[FORM_FIELDS.SHELF_LIFE_DISPLAY] || DEFAULT_VALUES.SHELF_LIFE_DISPLAY
      ],
      [FORM_FIELDS.SHELF_LIFE_EMOJI]: [
        ingredient[FORM_FIELDS.SHELF_LIFE_EMOJI] || DEFAULT_VALUES.SHELF_LIFE_EMOJI
      ],
      [FORM_FIELDS.SELECTED_PRODUCT]: this.fb.group({
        [FORM_FIELDS.PRODUCT_NAME]: [
          ingredient[FORM_FIELDS.SELECTED_PRODUCT]?.[FORM_FIELDS.PRODUCT_NAME] || DEFAULT_VALUES.EMPTY_STRING
        ],
        [FORM_FIELDS.STORE]: [
          ingredient[FORM_FIELDS.SELECTED_PRODUCT]?.[FORM_FIELDS.STORE] || DEFAULT_VALUES.EMPTY_STRING
        ],
        [FORM_FIELDS.PRICE]: [
          ingredient[FORM_FIELDS.SELECTED_PRODUCT]?.[FORM_FIELDS.PRICE] ?? null,
          [Validators.min(0)]
        ],
        [FORM_FIELDS.SIZE]: [
          ingredient[FORM_FIELDS.SELECTED_PRODUCT]?.[FORM_FIELDS.SIZE] || DEFAULT_VALUES.EMPTY_STRING
        ],
        [FORM_FIELDS.HEALTH_SCORE]: [
          ingredient[FORM_FIELDS.SELECTED_PRODUCT]?.[FORM_FIELDS.HEALTH_SCORE] ?? DEFAULT_VALUES.HEALTH_SCORE_DEFAULT,
          [Validators.min(DEFAULT_VALUES.HEALTH_SCORE_MIN), Validators.max(DEFAULT_VALUES.HEALTH_SCORE_MAX)]
        ],
        [FORM_FIELDS.REASON]: [
          ingredient[FORM_FIELDS.SELECTED_PRODUCT]?.[FORM_FIELDS.REASON] || DEFAULT_VALUES.EMPTY_STRING
        ],
        [FORM_FIELDS.GREEN_FLAGS]: [
          ingredient[FORM_FIELDS.SELECTED_PRODUCT]?.[FORM_FIELDS.GREEN_FLAGS] || DEFAULT_VALUES.EMPTY_ARRAY
        ],
        [FORM_FIELDS.RED_FLAGS]: [
          ingredient[FORM_FIELDS.SELECTED_PRODUCT]?.[FORM_FIELDS.RED_FLAGS] || DEFAULT_VALUES.EMPTY_ARRAY
        ]
      }),
      [FORM_FIELDS.ALTERNATIVES]: [ingredient[FORM_FIELDS.ALTERNATIVES] || DEFAULT_VALUES.EMPTY_ARRAY]
    });
  }

  getTotalApproxAmount(): number {
    if (!this.groceryForm) {
      return 0;
    }

    let total = 0;
    this.ingredientsFormArray.controls.forEach(control => {
      const selectedGroup = control.get(FORM_FIELDS.SELECTED_PRODUCT) as FormGroup | null;
      const price = selectedGroup?.get(FORM_FIELDS.PRICE)?.value;
      const num = this.toNumber(price);
      if (num > 0) {
        total += num;
      }
    });
    return total;
  }

  getTotalApproxSavings(): number {
    if (!this.groceryForm) {
      return 0;
    }

    let savings = 0;
    this.ingredientsFormArray.controls.forEach(control => {
      const selectedGroup = control.get(FORM_FIELDS.SELECTED_PRODUCT) as FormGroup | null;
      const selectedPrice = this.toNumber(selectedGroup?.get(FORM_FIELDS.PRICE)?.value);
      const alternatives = control.get(FORM_FIELDS.ALTERNATIVES)?.value ?? [];

      if (!Array.isArray(alternatives) || selectedPrice <= 0) {
        return;
      }

      const altPrices = alternatives
        .map((alt: any) => this.toNumber(alt?.[FORM_FIELDS.PRICE]))
        .filter((val: number) => val > 0);

      if (!altPrices.length) {
        return;
      }

      const baseline = Math.max(...altPrices);
      if (baseline > selectedPrice) {
        savings += baseline - selectedPrice;
      }
    });

    return savings;
  }

  private toNumber(value: any): number {
    if (typeof value === 'number') {
      return value;
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) {
        return 0;
      }
      const numeric = Number(trimmed.replace(/[^0-9.\-]/g, ''));
      return isNaN(numeric) ? 0 : numeric;
    }
    return 0;
  }

  selectAlternative(ingredientIndex: number, alternativeIndex: number) {
    const alternatives = this.getIngredientValue(ingredientIndex, FORM_FIELDS.ALTERNATIVES);
    if (!alternatives || !Array.isArray(alternatives) || alternativeIndex >= alternatives.length) {
      this.toastService.errorMessage('Invalid alternative selected.');
      return;
    }

    const alternative = alternatives[alternativeIndex];
    const selectedProductGroup = this.ingredientsFormArray.at(ingredientIndex)?.get(FORM_FIELDS.SELECTED_PRODUCT) as FormGroup;
    
    if (selectedProductGroup) {
      selectedProductGroup.patchValue({
        [FORM_FIELDS.PRODUCT_NAME]: alternative[FORM_FIELDS.PRODUCT_NAME] || DEFAULT_VALUES.EMPTY_STRING,
        [FORM_FIELDS.STORE]: alternative[FORM_FIELDS.STORE] || DEFAULT_VALUES.EMPTY_STRING,
        [FORM_FIELDS.PRICE]: alternative[FORM_FIELDS.PRICE] ?? null,
        [FORM_FIELDS.SIZE]: alternative[FORM_FIELDS.SIZE] || DEFAULT_VALUES.EMPTY_STRING,
        [FORM_FIELDS.HEALTH_SCORE]: alternative[FORM_FIELDS.HEALTH_SCORE] ?? DEFAULT_VALUES.HEALTH_SCORE_DEFAULT,
        [FORM_FIELDS.REASON]: alternative[FORM_FIELDS.REASON] || DEFAULT_VALUES.EMPTY_STRING,
        [FORM_FIELDS.GREEN_FLAGS]: DEFAULT_VALUES.EMPTY_ARRAY,
        [FORM_FIELDS.RED_FLAGS]: DEFAULT_VALUES.EMPTY_ARRAY
      });
      this.toastService.successMessage('Alternative product selected successfully!');
    }
  }

  onSave() {
    if (this.groceryForm.invalid) {
      // Mark all fields as touched to show validation errors
      this.groceryForm.markAllAsTouched();
      this.toastService.errorMessage('Please fix the form errors before saving.');
      
      // Log specific errors for debugging
      const errors = this.getFormErrors();
      if (errors.length > 0) {
        console.warn('Form validation errors:', errors);
      }
      return;
    }

    this.saving = true;
    const formValue = this.groceryForm.value;

    const payload = {
      member_id: this.data.memberId,
      ingredients: formValue[FORM_FIELDS.INGREDIENTS]
    };

    // Call grocery API directly - CORS is configured
    const sub = this.http.post(`${this.groceryApiUrl}/save_grocery_edits`, payload)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          this.handleError('save', error);
          return throwError(() => error);
        }),
        finalize(() => {
          this.saving = false;
        })
      )
      .subscribe({
        next: (_response: unknown) => {
          // Mark form as pristine after successful save
          this.groceryForm.markAsPristine();
          this.toastService.successMessage('Your grocery list has been saved successfully!');
        },
        error: () => {
          // Error already handled in catchError operator
        }
      });
    this.subscriptions.push(sub);
  }

  private getFormErrors(): string[] {
    const errors: string[] = [];
    const ingredientsArray = this.ingredientsFormArray;

    ingredientsArray.controls.forEach((control, index: number) => {
      const ingredientControl = control as FormGroup;

      const ingredientErrors = ingredientControl.errors;
      if (ingredientErrors) {
        errors.push(`Ingredient ${index + 1} has errors`);
     }

      const selectedProductGroup = ingredientControl.get(FORM_FIELDS.SELECTED_PRODUCT) as FormGroup;
      if (selectedProductGroup && selectedProductGroup.errors) {
        errors.push(`Ingredient ${index + 1} selected product has errors`);
      }
    });

    return errors;
  }

  onDownloadPDF() {
    // First save if there are changes, then download
    if (this.groceryForm.dirty) {
      if (this.groceryForm.invalid) {
        this.groceryForm.markAllAsTouched();
        this.toastService.errorMessage('Please fix form errors before downloading.');
        return;
      }

      this.saving = true;
      const formValue = this.groceryForm.value;
      const payload = {
        member_id: this.data.memberId,
        ingredients: formValue[FORM_FIELDS.INGREDIENTS]
      };

      // Chain save and download operations using observables
      // Call grocery API directly - CORS is configured
      const saveSub = this.http.post(`${this.groceryApiUrl}/save_grocery_edits`, payload)
        .pipe(
          catchError((error: HttpErrorResponse) => {
            this.handleError('save', error);
            this.saving = false;
            return throwError(() => error);
          })
        )
        .subscribe({
          next: () => {
            // After successful save, proceed with download
            this.groceryForm.markAsPristine();
            this.downloadPDF();
          },
          error: () => {
            // Error already handled
          }
        });
      this.subscriptions.push(saveSub);
    } else {
      this.downloadPDF();
    }
  }

  private downloadPDF() {
    // Call grocery API directly - CORS is configured
    const sub = this.http.post(`${this.groceryApiUrl}/download_pdf`, 
      { member_id: this.data.memberId }, 
      { responseType: 'blob' }
    )
      .pipe(
        catchError((error: HttpErrorResponse) => {
          this.handleError('download', error);
          return throwError(() => error);
        }),
        finalize(() => {
          this.saving = false;
        })
      )
      .subscribe({
        next: (blob: Blob) => {
          try {
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `grocery_list_${this.data.memberId}_${Date.now()}.pdf`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
            this.toastService.successMessage('PDF downloaded successfully!');
          } catch (error) {
            console.error('Error creating download link:', error);
            this.toastService.errorMessage('Error initiating PDF download.');
          }
        },
        error: () => {
          // Error already handled in catchError operator
        }
      });
    this.subscriptions.push(sub);
  }

  onCancel() {
    if (this.groceryForm?.dirty) {
      // Use Material Dialog for confirmation instead of browser confirm
      const confirmDialogRef = this.dialog.open(ConfirmDialogComponent, {
        width: '400px',
        data: {
          title: 'Unsaved Changes',
          message: 'You have unsaved changes. Are you sure you want to close?',
          confirmText: 'Yes, Close',
          cancelText: 'Cancel'
        }
      });

      const confirmSub = confirmDialogRef.afterClosed().subscribe((result: boolean | undefined) => {
        if (result === true) {
          this.dialogRef.close();
        }
      });
      this.subscriptions.push(confirmSub);
    } else {
      this.dialogRef.close();
    }
  }
}

// Simple confirmation dialog component
@Component({
  selector: 'app-confirm-dialog',
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatButtonModule],
  template: `
    <h2 mat-dialog-title>{{ data.title }}</h2>
    <mat-dialog-content>
      <p>{{ data.message }}</p>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button [mat-dialog-close]="false">{{ data.cancelText || 'Cancel' }}</button>
      <button mat-raised-button color="primary" [mat-dialog-close]="true">
        {{ data.confirmText || 'Confirm' }}
      </button>
    </mat-dialog-actions>
  `
})
export class ConfirmDialogComponent {
  constructor(@Inject(MAT_DIALOG_DATA) public data: { 
    title: string; 
    message: string; 
    confirmText?: string; 
    cancelText?: string; 
  }) {}
}

